import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BRAND } from "@/data/products";

const PRINCIPLES = [
  {
    num: "01",
    title: "Choosing carefully",
    text: "We source our spices with a focus on quality and consistency — comparing before we commit.",
  },
  {
    num: "02",
    title: "Handling responsibly",
    text: "We pay attention to how spices are processed and handled, keeping cleanliness and character intact.",
  },
  {
    num: "03",
    title: "Packing for freshness",
    text: "We pack with freshness in mind so natural flavour and aroma are protected until they reach your kitchen.",
  },
];

const Story = () => {
  return (
    <div data-testid="story-page" className="bg-cream min-h-screen">
      {/* Hero */}
      <section className="relative h-[72vh] w-full overflow-hidden flex items-end">
        <div className="absolute inset-0">
          <img
            src={BRAND.storyImage}
            alt="A good spoonful of spice"
            className="w-full h-full object-cover animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/20" />
        </div>
        <div className="relative px-6 sm:px-12 lg:px-24 pb-20">
          <p data-testid="story-eyebrow" className="eyebrow animate-fade-in-up">
            Our Story
          </p>
          <h1
            data-testid="story-title"
            className="font-display text-5xl sm:text-6xl md:text-7xl font-bold text-cream mt-6 max-w-3xl leading-[1] animate-fade-in-up delay-100"
          >
            Better food begins with{" "}
            <span className="text-coral">better ingredients.</span>
          </h1>
        </div>
      </section>

      {/* Narrative */}
      <section className="relative overflow-hidden px-6 sm:px-12 lg:px-24 py-24 sm:py-32">
        <div className="absolute inset-0 pattern-bg opacity-60 pointer-events-none" />
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-24">
          <div className="md:col-span-4">
            <p className="font-primary font-bold text-2xl sm:text-3xl text-ink leading-snug md:sticky md:top-32">
              A simple idea — naturally better spices for everyday kitchens.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6 flex flex-col gap-6 font-primary text-lg text-ink/70 leading-relaxed">
            <p>
              At {BRAND.name}, we believe better food begins with better
              ingredients.
            </p>
            <p>
              Spices may be a small part of a recipe, but they can shape the
              flavour, aroma and character of an entire meal. That is why we
              believe they deserve care at every step of their journey — from
              sourcing and selection to processing, handling and packaging.
            </p>
            <p>
              We started {BRAND.name} with a simple idea: to make naturally
              better spices for everyday kitchens.
            </p>
            <p>
              We carefully source our spices with a focus on quality and
              consistency. We pay attention to how they are processed and
              handled, and we pack them with freshness in mind — so their
              natural flavour and aroma are better protected until they reach
              your kitchen spoon.
            </p>
          </div>
        </div>
      </section>

      {/* Principles — dark section */}
      <section className="relative bg-ink text-cream overflow-hidden">
        <div className="absolute inset-0 pattern-bg opacity-60 pointer-events-none" />
        <div className="relative px-6 sm:px-12 lg:px-24 py-24">
          <p className="eyebrow">What "Good" Means To Us</p>
          <h2 className="font-primary font-bold text-4xl sm:text-5xl text-cream mt-5 max-w-2xl leading-tight">
            Doing the important things well, every day.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16">
            {PRINCIPLES.map((p) => (
              <div
                key={p.num}
                data-testid={`story-principle-${p.num}`}
                className="bg-cream/[0.04] border border-cream/10 p-8 hover:border-coral/50 transition-colors duration-300"
              >
                <span className="font-display text-5xl font-bold text-coral">
                  {p.num}
                </span>
                <h3 className="font-primary font-bold text-xl text-cream mt-6">
                  {p.title}
                </h3>
                <p className="font-primary text-cream/60 mt-3 leading-relaxed text-sm">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing statement */}
      <section className="px-6 sm:px-12 lg:px-24 py-28 sm:py-40 text-center">
        <p className="font-primary font-medium text-2xl sm:text-3xl md:text-4xl text-ink max-w-4xl mx-auto leading-snug">
          Because every memorable meal begins with the ingredients that go into
          it. And sometimes, something as simple as a{" "}
          <span className="text-coral font-bold">good spoonful</span> can make
          all the difference.
        </p>
        <p className="font-display text-3xl font-bold text-ink mt-14">
          {BRAND.name}
        </p>
        <p className="eyebrow mt-3">Naturally Better Spices</p>

        <div className="mt-12 flex justify-center">
          <Link to="/products" data-testid="story-cta-products" className="btn-primary group">
            Explore the Spices
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Story;
